import { useState } from 'react'
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'
import { Loader2, Trash2, UserX, ArrowLeft } from 'lucide-react'
import type { Account, Report } from '@olimhub/shared'
import { adminApi } from '@/api'
import type { AdminStats } from '@/api/endpoints/admin'
import { Avatar } from '@/components/profile/Avatar'
import { formatPostTime } from '@/lib/dates'
import { accountPath } from '@/lib/accountPath'
import { useToast } from '@/components/common/Toast'

type AdminReportsTab = 'reports' | 'new-users'

interface ReportCardProps {
  report: Report
  onAction: (id: string, status: 'reviewed' | 'dismissed') => void
  onDelete: (postId: string, reportId: string) => void
  onBan: (accountId: string, username: string, reportId: string) => void
}

function ReportCard({ report, onAction, onDelete, onBan }: ReportCardProps) {
  const { t } = useTranslation()

  const postAuthor = report.reportedPost?.account ?? null
  const bannableUser = report.reportedUser ?? postAuthor

  return (
    <div
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        overflow: 'hidden',
      }}
    >
      {/* Report metadata */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Reporter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar name={report.reporter.displayName} avatarUrl={report.reporter.avatarUrl} animalAvatar={report.reporter.animalAvatar} size="sm" />
          <div>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
              {t('admin.reporter')}:
            </span>{' '}
            <strong style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)', fontWeight: 700 }}>
              @{report.reporter.username}
            </strong>
          </div>
          <span style={{ marginInlineStart: 'auto', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
            {formatPostTime(report.createdAt)}
          </span>
        </div>

        {/* Reason */}
        <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px' }}>
          {report.reason}
        </p>

        {/* Reported post excerpt */}
        {report.reportedPost && (
          <div style={{ borderInlineStart: '3px solid var(--border-strong)', paddingInlineStart: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
              <Avatar name={postAuthor!.displayName} avatarUrl={postAuthor!.avatarUrl} animalAvatar={postAuthor!.animalAvatar} size="sm" />
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--text-strong)' }}>
                @{postAuthor!.username}
              </span>
            </div>
            <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {report.reportedPost.content}
            </p>
          </div>
        )}

        {/* Reported user (user-only reports) */}
        {report.reportedUser && !report.reportedPost && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar name={report.reportedUser.displayName} avatarUrl={report.reportedUser.avatarUrl} animalAvatar={report.reportedUser.animalAvatar} size="sm" />
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)', fontWeight: 700 }}>
              @{report.reportedUser.username}
            </span>
          </div>
        )}
      </div>

      {/* Action strip */}
      {report.status === 'pending' && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            padding: '10px 16px',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            background: 'color-mix(in srgb, var(--surface-2) 60%, var(--surface))',
          }}
        >
          {/* Moderation actions */}
          {report.reportedPost && (
            <button
              onClick={() => onDelete(report.reportedPost!.id, report.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1.5px solid var(--danger)', borderRadius: 'var(--radius-pill)', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-xs)', color: 'var(--danger)' }}
            >
              <Trash2 size={13} />
              {t('admin.delete_post')}
            </button>
          )}
          {bannableUser && (
            <button
              onClick={() => onBan(bannableUser.id, bannableUser.username, report.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1.5px solid var(--border-strong)', borderRadius: 'var(--radius-pill)', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-xs)', color: 'var(--text-body)' }}
            >
              <UserX size={13} />
              Ban @{bannableUser.username}
            </button>
          )}

          {/* Review actions — pushed to end */}
          <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8 }}>
            <button
              onClick={() => onAction(report.id, 'dismissed')}
              style={{ padding: '6px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-pill)', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}
            >
              {t('admin.dismiss')}
            </button>
            <button
              onClick={() => onAction(report.id, 'reviewed')}
              style={{ padding: '6px 12px', border: 'none', borderRadius: 'var(--radius-pill)', background: 'var(--brand)', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-xs)', color: 'var(--on-brand)' }}
            >
              {t('admin.mark_reviewed')}
            </button>
          </div>
        </div>
      )}
      {report.status !== 'pending' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '8px 16px', textAlign: 'right', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
          {report.status === 'reviewed' ? t('admin.reviewed') : t('admin.dismissed')}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, loading }: { label: string; value: number | undefined; loading: boolean }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius-md)',
        padding: '10px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-muted)',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      {loading ? (
        <div
          style={{
            height: 22,
            width: 48,
            borderRadius: 4,
            background: 'var(--border)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ) : (
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-lg)',
            fontWeight: 700,
            color: 'var(--text-strong)',
            lineHeight: 1.2,
          }}
        >
          {value?.toLocaleString() ?? '—'}
        </span>
      )}
    </div>
  )
}

function StatsBar({ stats, loading }: { stats: AdminStats | undefined; loading: boolean }) {
  const { t } = useTranslation()
  return (
    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <StatCard label={t('admin.stats_total_members')} value={stats?.totalUsers} loading={loading} />
        <StatCard label={t('admin.stats_new_week')} value={stats?.newUsersLast7Days} loading={loading} />
        <StatCard label={t('admin.stats_total_posts')} value={stats?.totalPosts} loading={loading} />
        <StatCard label={t('admin.stats_active_today')} value={stats?.activeToday} loading={loading} />
      </div>
    </div>
  )
}

function NewUserRow({ account }: { account: Account }) {
  const { t } = useTranslation()
  return (
    <Link
      to={accountPath(account)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        textDecoration: 'none',
      }}
    >
      <Avatar name={account.displayName || account.username} avatarUrl={account.avatarUrl} animalAvatar={account.animalAvatar} size="md" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, color: 'var(--text-strong)', fontSize: 'var(--fs-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {account.displayName || account.username}
        </div>
        <div style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          @{account.username}
        </div>
      </div>
      <span style={{ flexShrink: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)', textAlign: 'right' }}>
        {t('admin.joined')}
        <br />
        {formatPostTime(account.createdAt)}
      </span>
    </Link>
  )
}

export function AdminReportsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [tab, setTab] = useState<AdminReportsTab>('reports')

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => adminApi.getStats(),
    staleTime: 60_000,
  })

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['admin', 'reports', 'pending'],
    queryFn: ({ pageParam }) => adminApi.getReports(pageParam as string | undefined, 'pending'),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: tab === 'reports',
  })

  const { data: recentUsers, isLoading: recentUsersLoading } = useQuery({
    queryKey: ['admin', 'users', 'recent'],
    queryFn: () => adminApi.getRecentUsers(),
    enabled: tab === 'new-users',
  })

  const allReports: Report[] = data?.pages.flatMap((p) => p.items) ?? []

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'reviewed' | 'dismissed' }) =>
      adminApi.updateReport(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reports'] })
      toast.success('Report updated')
    },
    onError: () => toast.error(t('common.error')),
  })

  const deleteMutation = useMutation({
    mutationFn: ({ postId, reportId }: { postId: string; reportId: string }) =>
      adminApi.deletePost(postId).then(() => adminApi.updateReport(reportId, 'reviewed')),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reports'] })
      toast.success('Post deleted and report resolved')
    },
    onError: () => toast.error(t('common.error')),
  })

  const banMutation = useMutation({
    mutationFn: ({ accountId, reportId }: { accountId: string; username: string; reportId: string }) =>
      adminApi.banAccount(accountId, false).then(() => adminApi.updateReport(reportId, 'reviewed')),
    onSuccess: (_, { username }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reports'] })
      toast.success(`@${username} banned and report resolved`)
    },
    onError: () => toast.error(t('common.error')),
  })

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate('/admin')}
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: 'var(--text-strong)' }}>
          {t('admin.title')}
        </h1>
      </div>

      <StatsBar stats={stats} loading={statsLoading} />

      {/* Tabs — same pattern as GroupPage's Feed/Members tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
        {(['reports', 'new-users'] as AdminReportsTab[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={{
              flex: 1,
              padding: '10px 0',
              border: 'none',
              borderBottom: tab === key ? '2px solid var(--brand)' : '2px solid transparent',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              color: tab === key ? 'var(--text-strong)' : 'var(--text-faint)',
            }}
          >
            {key === 'reports' ? t('admin.tab_pending_reports') : t('admin.tab_new_users')}
          </button>
        ))}
      </div>

      {tab === 'reports' ? (
        isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Loader2 size={24} style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : allReports.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            {t('admin.pending')}: 0
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' }}>
            {allReports.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                onAction={(id, status) => updateMutation.mutate({ id, status })}
                onDelete={(postId, reportId) => deleteMutation.mutate({ postId, reportId })}
                onBan={(accountId, username, reportId) => banMutation.mutate({ accountId, username, reportId })}
              />
            ))}

            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                style={{ width: '100%', padding: '12px 0', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', cursor: isFetchingNextPage ? 'not-allowed' : 'pointer' }}
              >
                {isFetchingNextPage ? t('feed.loading_more') : t('feed.load_more')}
              </button>
            )}
          </div>
        )
      ) : recentUsersLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Loader2 size={24} style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : !recentUsers || recentUsers.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          {t('admin.no_new_users')}
        </div>
      ) : (
        <div>
          {recentUsers.map((account) => (
            <NewUserRow key={account.id} account={account} />
          ))}
        </div>
      )}
    </div>
  )
}
